import type {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';

import {Badge} from '@/components/ui/badge';
import {Button} from '@/components/ui/button';
import {Checkbox} from '@/components/ui/checkbox';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import {cn} from '@/utils/ui';

import useEvacuationStore from '../stores/evacuationStore';
import EvacuationDisabledHint from './EvacuationDisabledHint';

type EvacuationPersonRowProps = {
  personId: string;
  name: string;
  details?: ReactNode;
  badge?: ReactNode;
};

const EvacuationPersonRow = ({
  personId,
  name,
  details,
  badge,
}: EvacuationPersonRowProps) => {
  const {t} = useTranslation();
  const confirmed = useEvacuationStore(s => s.confirmedIds[personId] ?? false);
  const absent = useEvacuationStore(s => s.absentIds[personId] ?? false);
  const toggleAbsent = useEvacuationStore(s => s.toggleAbsent);
  const toggleConfirmed = useEvacuationStore(s => s.toggleConfirmed);

  return (
    <Item
      variant="surface"
      role="listitem"
      className={cn('flex cursor-pointer items-center gap-3', {
        'cursor-default': absent,
      })}
      asChild
    >
      <label>
        <ItemMedia>
          <EvacuationDisabledHint
            reason={
              absent
                ? t('Skipped people cannot be confirmed. Include them first.')
                : undefined
            }
          >
            <Checkbox
              touchOptimized
              aria-label={name}
              checked={confirmed}
              disabled={absent}
              onCheckedChange={() => toggleConfirmed(personId)}
            />
          </EvacuationDisabledHint>
        </ItemMedia>
        <ItemContent className={cn({'opacity-25': confirmed || absent})}>
          <ItemTitle className={cn({'line-through': absent})}>
            {name}
            {badge}
          </ItemTitle>
          {details && <ItemDescription>{details}</ItemDescription>}
        </ItemContent>
        <ItemActions>
          {absent && (
            <Badge variant="filled" color="neutral">
              {t('Skipped')}
            </Badge>
          )}
          <EvacuationDisabledHint
            reason={
              confirmed
                ? t('Confirmed people cannot be skipped. Uncheck them first.')
                : undefined
            }
          >
            <Button
              type="button"
              variant="secondary"
              size="small"
              disabled={confirmed}
              onClick={() => toggleAbsent(personId)}
            >
              {absent ? t('Include') : t('Skip')}
            </Button>
          </EvacuationDisabledHint>
        </ItemActions>
      </label>
    </Item>
  );
};

export default EvacuationPersonRow;
